import MuiExpandLessIcon from "@mui/icons-material/ExpandLess";
import MuiExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import LanguageContext from "components/LanguageContext";
import categories from "components/templates/geolocalisationDsfr/geolocalisation.categories.enum.json";
import BadgeToDisplay from "components/templatesComponents/BadgeToDisplay";
import ExpansionText from "components/templatesComponents/ExpansionText";
import Icon from "components/templatesComponents/Icon";
import Link from "components/templatesComponents/Link";
import PropTypes from "prop-types";
import React, { forwardRef, useContext, useState } from "react";
import { normalizePhoneNumber } from "utils/commonUtils";
import t from "utils/locales/translation.json";

const InfoGeolocDsfr = forwardRef((props, ref) => {
  const {
    geoPointItem = {},
    distanceToDisplay = "",
    fullAddressToDisplay = "",
    source = null,
    department = null,
    activeCard = "",
    setActiveCard = null,
  } = props;

  const { type = "" } = geoPointItem._source || {};

  const { language } = useContext(LanguageContext);

  const { category = "", others } = geoPointItem._source || {};

  const { name = "", phone = "", webSite = "", modalities = "", label = "" } = others || {};

  const { basic, specific } = categories[type] || {};

  const [isToggleOn, setToggle] = useState(false);

  const week = (t[language].geolocalisation.week_days || "").split(",");

  const d = new Date();
  const today = d.getDay() - 1;

  const webSiteUrl = !webSite.startsWith("https://") && !webSite.startsWith("http://") ? `https://${webSite}` : webSite;

  const badgeTitle = category?.toLowerCase() === specific ? specific : basic;
  const badgeColor = category?.toLowerCase() === specific ? "pink-tuile" : "info";

  const iconDSFR = source === "mairie" ? "add-circle-fill" : "";

  return (
    <Grid
      key={geoPointItem._source.customId}
      ref={ref}
      onMouseLeave={() => {
        if (activeCard === geoPointItem._id) {
          setActiveCard("");
        }
      }}
      sx={{
        ...(activeCard === geoPointItem._id
          ? {
              "& > div": {
                borderColor: "var(--background-action-high-blue-france)",
                backgroundColor: "var(--light-background-alt-grey, #F6F6F6)",
              },
            }
          : {}),
      }}
      size={{ xs: 12, md: 6, lg: 4 }}
    >
      <Stack
        spacing={1}
        sx={{
          border: "1px solid var(--light-decisions-border-border-default-grey, #DDD)",
          bgcolor: "var(--light-background-default-grey, #FFF)",
          p: 3,
          height: "100%",
          "&:hover": { bgcolor: "var(--light-background-alt-grey, #F6F6F6)" },
        }}
      >
        <Stack spacing={1} direction="column-reverse">
          <h3 className="fr-h6 fr-mb-0">{name}</h3>
          <BadgeToDisplay
            label={badgeTitle === "passeport" ? "" : badgeTitle}
            color={badgeColor}
            iconDSFR={iconDSFR}
            className="fr-text--xs fr-px-1v fr-mb-2w"
          />
        </Stack>
        <p className="fr-text--sm">{fullAddressToDisplay}</p>
        {department ? null : <p className="fr-text--xs fr-mt-1w">{distanceToDisplay}</p>}
        {phone && (
          <Link
            url={`tel:${normalizePhoneNumber(phone)}`}
            iconDSFR="phone-line"
            iconPosition="left"
            className="fr-text--sm"
            sx={{ width: "fit-content" }}
          >
            {normalizePhoneNumber(phone)}
          </Link>
        )}
        {geoPointItem.opening &&
          geoPointItem.opening.length > 0 &&
          geoPointItem.opening.reduce((hasValue, opening) => hasValue || !!opening, false) && (
            <Grid
              container
              onClick={() => setToggle(!isToggleOn)}
              sx={{ cursor: "pointer", mt: { xs: 2, lg: 1 }, fontSize: "0.77rem" }}
            >
              <Grid sx={{ maxWidth: "87%" }} role="list">
                {Array.isArray(week) &&
                  week.map((day, i) => (
                    <div key={day} role="listitem">
                      {i === today ? (
                        <Box sx={{ display: "flex" }}>
                          <p className="fr-text--sm fr-mb-0">
                            {day} :{" "}
                            {!geoPointItem.opening[i] ? (
                              <i>{t[language].geolocalisation.no_schedule}</i>
                            ) : (
                              geoPointItem.opening[i]
                            )}
                          </p>{" "}
                        </Box>
                      ) : (
                        <Box component="p" className="fr-text--sm fr-mb-0" sx={{ display: isToggleOn ? "" : "none" }}>
                          {day} :{" "}
                          {!geoPointItem.opening[i] ? (
                            <i>{t[language].geolocalisation.no_schedule}</i>
                          ) : (
                            geoPointItem.opening[i]
                          )}
                        </Box>
                      )}
                    </div>
                  ))}
              </Grid>
              <Grid sx={{ ml: 0.5 }}>
                <Box
                  component="button"
                  type="button"
                  aria-expanded={isToggleOn}
                  title={
                    isToggleOn
                      ? t[language].geolocalisation.collapse_schedule
                      : t[language].geolocalisation.expand_schedule
                  }
                  sx={{ p: 0, display: "flex" }}
                >
                  {isToggleOn ? <MuiExpandLessIcon /> : <MuiExpandMoreIcon />}
                </Box>
              </Grid>
            </Grid>
          )}
        {source === "mairie" && category?.toLowerCase() === "certification" && label === "avec rdv" && (
          <Stack direction="row" spacing={1} sx={{ position: "relative" }}>
            <Icon iconDSFR="calendar-line" size="sm" />
            <Icon iconDSFR="time-fill" size="sm" sx={{ position: "absolute", top: "6px", left: "1px" }} />
            <p className="fr-text--xs">
              {t[language].geolocalisation.certification_label_1}
              <b>{t[language].geolocalisation.certification_label_2}</b>
            </p>
          </Stack>
        )}
        {modalities && (
          <ExpansionText
            reducedLabel={t[language].geolocalisation.expanded_label}
            expandedLabel={t[language].geolocalisation.expanded_label}
            text={<p className="fr-text--xs">{modalities}</p>}
            labelBefore
            sx={{ "& span": { fontSize: "0.75rem" } }}
          />
        )}
        {webSite && (
          <Link
            basic
            button
            type="button"
            external
            className="fr-btn fr-btn--secondary fr-btn--sm fr-mt-3w"
            url={webSiteUrl}
            title={`${t[language].geolocalisation.site} - ${t[language].components.new_tab}`}
            sx={{ "a&:hover": { color: "primary.main" } }}
          >
            {t[language].geolocalisation.site}
          </Link>
        )}
      </Stack>
    </Grid>
  );
});

InfoGeolocDsfr.propTypes = {
  geoPointItem: PropTypes.shape().isRequired,
  distanceToDisplay: PropTypes.string,
  fullAddressToDisplay: PropTypes.string,
  source: PropTypes.string,
  department: PropTypes.shape(),
  activeCard: PropTypes.string,
  setActiveCard: PropTypes.func,
};

export default InfoGeolocDsfr;
